<template>
  <div class="print-logs-page">
    <!-- Page Header -->
    <div class="panel panel-flat">
      <div class="panel-heading print-logs-header">
        <div class="header-left">
          <img
            src="../../assets/images/black_shipzone.png"
            alt="Lasshopping"
            class="header-logo"
          />
          <div class="header-title-block">
            <h3 class="header-title">{{ $t('printLogs.title') }}</h3>
            <p class="header-subtitle">{{ $t('printLogs.subtitle') }}</p>
          </div>
        </div>
        <div class="header-actions no-print">
          <button class="btn btn-print-page" @click="printPage">
            <i class="fa fa-print"></i> {{ $t('printLogs.printButton') }}
          </button>
        </div>
      </div>

      <div class="panel-body">
        <!-- Date & Type Filter -->
        <div class="filter-row no-print">
          <label class="filter-label">
            <i class="fa fa-calendar"></i> {{ $t('printLogs.fromLabel') }}
          </label>
          <input
            type="date"
            class="form-control date-input"
            v-model="filterFrom"
            @change="fetchLogs"
          />
          <label class="filter-label">{{ $t('printLogs.toLabel') }}</label>
          <input
            type="date"
            class="form-control date-input"
            v-model="filterTo"
            @change="fetchLogs"
          />
          <label class="filter-label">
            <i class="fa fa-print"></i> {{ $t('printLogs.typeLabel') }}
          </label>
          <select
            class="form-control type-input"
            v-model="filterType"
            @change="fetchLogs"
          >
            <option value="">{{ $t('printLogs.allTypesOption') }}</option>
            <option value="shipping">{{ $t('columns.shipping') }}</option>
            <option value="second_shipping">{{ $t('printLogs.secondShippingOption') }}</option>
            <option value="delivery">{{ $t('sidebar.delivery') }}</option>
          </select>
          <label class="filter-label">
            <i class="fa fa-user"></i> {{ $t('printLogs.printedByLabel') }}
          </label>
          <v-select
            :items="printedByOptions"
            v-model="filterPrintedBy"
            @input="fetchLogs"
            autocomplete
            :label="$t('printLogs.allStaffOption')"
            class="printed-by-select"
            clearable
            hide-details
            single-line
          ></v-select>
          <button class="btn btn-primary btn-filter" @click="fetchLogs">
            <i class="fa fa-search"></i> {{ $t('printLogs.filterButton') }}
          </button>
          <button class="btn btn-default btn-filter-today" @click="goToday">
            <i class="fa fa-calendar-o"></i> {{ $t('printLogs.todayButton') }}
          </button>
        </div>

        <!-- Summary Bar -->
        <div class="summary-bar">
          <div class="summary-item">
            <i class="fa fa-list-ul summary-icon"></i>
            <span class="summary-label">{{ $t('printLogs.totalEntries') }}</span>
            <span class="summary-value">{{ logs.length }}</span>
          </div>
          <div class="summary-item">
            <i class="fa fa-dollar summary-icon"></i>
            <span class="summary-label">{{ $t('printLogs.totalAmountLabel') }}</span>
            <span class="summary-value total-amount-value">${{ totalAmount }}</span>
          </div>
          <div class="summary-item">
            <i class="fa fa-print summary-icon"></i>
            <span class="summary-label">{{ $t('printLogs.firstPrints') }}</span>
            <span class="summary-value">{{ firstPrintsCount }}</span>
          </div>
          <div class="summary-item">
            <i class="fa fa-refresh summary-icon"></i>
            <span class="summary-label">{{ $t('printLogs.reprints') }}</span>
            <span class="summary-value reprint-value">{{ reprintsCount }}</span>
          </div>
        </div>

        <!-- Loading Spinner -->
        <div v-if="loading" class="loading-container">
          <div class="spinner-wrap">
            <i class="fa fa-circle-o-notch fa-spin fa-3x"></i>
            <p>{{ $t('printLogs.loadingLogsText') }}</p>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else-if="!loading && logs.length === 0" class="empty-state">
          <i class="fa fa-inbox empty-icon"></i>
          <h4>{{ $t('printLogs.noPrintsFoundTitle') }}</h4>
          <p>{{ $t('printLogs.noPrintsFoundBetween', { from: filterFrom, to: filterTo }) }}</p>
        </div>

        <!-- Data Table -->
        <div v-else class="table-responsive">
          <table class="table table-bordered table-striped table-hover logs-table">
            <thead>
              <tr class="table-header-row">
                <th class="col-num">#</th>
                <th class="col-customer">{{ $t('columns.customerName') }}</th>
                <th class="col-invoice">{{ $t('columns.invoiceNumber') }}</th>
                <th class="col-type">{{ $t('columns.type') }}</th>
                <th class="col-status">{{ $t('columns.status') }}</th>
                <th class="col-amount">{{ $t('columns.amount') }}</th>
                <th class="col-date">{{ $t('common.date') }}</th>
                <th class="col-time">{{ $t('printLogs.timeColumn') }}</th>
                <th class="col-user">{{ $t('printLogs.printedByLabel') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(log, index) in logs" :key="log.id" :class="getRowClass(log)">
                <td class="col-num text-center">{{ index + 1 }}</td>
                <td class="col-customer">
                  <i class="fa fa-user text-muted rtl-mr-5"></i>
                  {{ log.customer_name }}
                </td>
                <td class="col-invoice">
                  <strong>{{ log.invoice_number }}</strong>
                </td>
                <td class="col-type text-center">
                  <span :class="getTypeBadgeClass(log.invoice_type)">
                    {{ getTypeLabel(log.invoice_type) }}
                  </span>
                </td>
                <td class="col-status text-center">
                  <span :class="log.print_count > 1 ? 'badge badge-reprint' : 'badge badge-first'">
                    <i :class="log.print_count > 1 ? 'fa fa-refresh' : 'fa fa-check'"></i>
                    {{ log.print_count > 1 ? $t('printLogs.reprintBadge', { count: log.print_count }) : $t('printLogs.firstPrintBadge') }}
                  </span>
                  <span v-if="log.is_view_only" class="badge badge-view-only" :title="$t('printLogs.viewOnlyTooltip')">
                    <i class="fa fa-eye"></i> {{ $t('printLogs.viewOnlyBadge') }}
                  </span>
                  <span v-else-if="log.is_delivery" class="badge badge-delivery-print" :title="$t('printLogs.deliveryPrintTooltip')">
                    <i class="fa fa-motorcycle"></i> {{ $t('sidebar.delivery') }}
                  </span>
                  <span v-else class="badge badge-save-and-view" :title="$t('printLogs.saveAndViewTooltip')">
                    <i class="fa fa-check-circle"></i> {{ $t('printLogs.saveAndViewBadge') }}
                  </span>
                </td>
                <td class="col-amount text-right">
                  <strong>${{ parseFloat(log.total_amount).toFixed(2) }}</strong>
                </td>
                <td class="col-date">{{ formatDate(log.created_at) }}</td>
                <td class="col-time">{{ formatTime(log.created_at) }}</td>
                <td class="col-user">
                  <i class="fa fa-user-circle text-muted rtl-mr-5"></i>
                  {{ log.printed_by || '—' }}
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="table-footer-row">
                <th colspan="5" class="text-right">{{ $t('printLogs.totalLabel') }}</th>
                <th class="text-right total-cell">${{ totalAmount }}</th>
                <th colspan="3"></th>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>

    <!-- Print-only header (shows only when printing) -->
    <div class="print-only-header print-only">
      <img src="../../assets/images/black_shipzone.png" alt="Lasshopping" style="height:60px" />
      <div>
        <h2>Lasshopping — {{ $t('printLogs.title') }}</h2>
        <p>{{ $t('common.date') }}: {{ filterFrom }} — {{ filterTo }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { RTL_LOCALES } from '../lang'

export default {
  name: 'PrintLogs',

  data() {
    const today = new Date().toISOString().split('T')[0]
    return {
      filterFrom: today,
      filterTo: today,
      filterType: '',
      filterPrintedBy: null,
      printedByOptions: [],
      logs: [],
      totalAmount: '0.00',
      loading: false,
    }
  },

  computed: {
    firstPrintsCount() {
      return this.logs.filter(l => l.print_count == 1).length
    },
    reprintsCount() {
      return this.logs.filter(l => l.print_count > 1).length
    },
  },

  mounted() {
    this.fetchLogs()
    this.fetchPrintedByList()
  },

  methods: {
    fetchPrintedByList() {
      const token = localStorage.getItem('access_token')
      axios
        .get('api/print-logs/printed-by-list', {
          headers: { Authorization: 'Bearer ' + token },
        })
        .then(response => {
          this.printedByOptions = response.data.data
        })
        .catch(() => {
          this.printedByOptions = []
        })
    },

    fetchLogs() {
      this.loading = true
      const token = localStorage.getItem('access_token')
      axios
        .get('api/print-logs', {
          params: {
            from: this.filterFrom,
            to: this.filterTo,
            type: this.filterType || undefined,
            printed_by: this.filterPrintedBy || undefined
          },
          headers: { Authorization: 'Bearer ' + token },
        })
        .then(response => {
          this.logs = response.data.data
          this.totalAmount = response.data.total_amount
        })
        .catch(() => {
          this.logs = []
          this.totalAmount = '0.00'
        })
        .finally(() => {
          this.loading = false
        })
    },

    goToday() {
      const today = new Date().toISOString().split('T')[0]
      this.filterFrom = today
      this.filterTo = today
      this.fetchLogs()
    },

    printPage() {
      const tableEl = this.$el.querySelector('.table-responsive')
      if (!tableEl) return
      // This print window is entirely self-contained (its own <style> block, no
      // link to print_style.css or any other app stylesheet), so dir/lang and the
      // text-direction default both have to be set here explicitly rather than
      // relying on the RTL build pipeline.
      const isRtl = RTL_LOCALES.includes(this.$i18n.locale)
      const win = window.open('', '_blank')
      win.document.write(`
        <html dir="${isRtl ? 'rtl' : 'ltr'}" lang="${this.$i18n.locale}"><head><title>${this.$t('printLogs.title')}</title>
        <style>
          body { font-family: Arial, sans-serif; font-size: 12px; margin: 15px; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: ${isRtl ? 'right' : 'left'}; }
          thead tr th { background-color: #1a2a4a; color: #fff; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          tfoot tr th { background-color: #f0f4f8; font-weight: bold; }
          .badge { display: inline-block; padding: 2px 7px; border-radius: 10px; font-size: 11px; }
          .badge-first { background: #d4edda; color: #155724; }
          .badge-reprint { background: #ffe5b4; color: #7a4800; }
          .badge-view-only { background: #f1e6fb; color: #5a2d82; }
          .badge-save-and-view { background: #d4edda; color: #155724; }
          .badge-delivery-print { background: #cce5ff; color: #004085; }
          .badge-shipping { background: #d4edda; color: #155724; }
          .badge-second-shipping { background: #fff3cd; color: #856404; }
          .badge-delivery { background: #cce5ff; color: #004085; }
          .row-reprint { background-color: #fff8f0; }
          h3 { margin: 0 0 10px; font-size: 15px; }
        </style>
        </head><body>
        <h3>${this.$t('printLogs.printPageTitle', { from: this.filterFrom, to: this.filterTo })}</h3>
        ${tableEl.innerHTML}
        </body></html>
      `)
      win.document.close()
      win.focus()
      win.addEventListener('afterprint', () => win.close())
      win.print()
    },

    formatDate(datetime) {
      if (!datetime) return '—'
      // Backend returns UTC ISO timestamps; convert to the viewer's local time
      // instead of slicing the raw string, otherwise the displayed time is off
      // by the UTC offset (e.g. 3 hours behind for Asia/Baghdad).
      const date = new Date(datetime)
      if (isNaN(date.getTime())) return datetime.split('T')[0] || datetime.split(' ')[0]
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },

    formatTime(datetime) {
      if (!datetime) return '—'
      const date = new Date(datetime)
      if (isNaN(date.getTime())) return '—'
      return date.toLocaleTimeString('en-GB')
    },

    getTypeLabel(type) {
      const map = {
        shipping: this.$t('columns.shipping'),
        second_shipping: this.$t('printLogs.secondShippingOption'),
        delivery: this.$t('sidebar.delivery'),
      }
      return map[type] || type
    },

    getTypeBadgeClass(type) {
      const map = {
        shipping: 'badge badge-shipping',
        second_shipping: 'badge badge-second-shipping',
        delivery: 'badge badge-delivery',
      }
      return map[type] || 'badge badge-default'
    },

    getRowClass(log) {
      return log.print_count > 1 ? 'row-reprint' : 'row-first'
    },
  },
}
</script>

<style scoped>
/* ─── Page Layout ─────────────────────────────────────── */
.print-logs-page {
  padding: 15px;
}

/* ─── Panel Header ────────────────────────────────────── */
.print-logs-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: linear-gradient(135deg, #1a2a4a 0%, #2c4a7c 100%);
  color: #fff;
  border-radius: 4px 4px 0 0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-logo {
  height: 52px;
  width: 52px;
  border-radius: 8px;
  background: #fff;
  padding: 4px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.3);
}

.header-title-block {
  display: flex;
  flex-direction: column;
}

.header-title {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: #fff;
  line-height: 1.2;
}

.header-subtitle {
  margin: 2px 0 0;
  font-size: 12px;
  color: rgba(255,255,255,0.75);
}

.btn-print-page {
  background: #009688 !important;
  color: #fff !important;
  border: 1px solid #00796b !important;
  border-radius: 4px;
  padding: 6px 14px;
  font-size: 13px;
}
.btn-print-page:hover {
  background: #00796b !important;
  color: #fff !important;
}

/* ─── Filter Row ──────────────────────────────────────── */
.filter-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.filter-label {
  font-weight: 600;
  font-size: 14px;
  color: #555;
  white-space: nowrap;
}

.date-input {
  width: 180px !important;
  display: inline-block !important;
  height: 34px !important;
  font-size: 14px;
}

.type-input {
  width: 160px !important;
  display: inline-block !important;
  height: 34px !important;
  font-size: 14px;
}

.printed-by-select {
  width: 200px;
  display: inline-block;
  font-size: 14px;
}

.btn-filter {
  font-size: 13px;
  height: 34px;
  padding: 0 14px;
}

.btn-filter-today {
  font-size: 13px;
  height: 34px;
  padding: 0 14px;
}

/* ─── Summary Bar ─────────────────────────────────────── */
.summary-bar {
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f7f9fc;
  border: 1px solid #dde3eb;
  border-radius: 8px;
  padding: 10px 18px;
  min-width: 150px;
}

.summary-icon {
  font-size: 20px;
  color: #2c4a7c;
}

.summary-label {
  font-size: 12px;
  color: #888;
  display: block;
  line-height: 1;
}

.summary-value {
  font-size: 18px;
  font-weight: 700;
  color: #333;
}

.total-amount-value {
  color: #1a7a1a;
}

.reprint-value {
  color: #c26f02;
}

/* ─── Loading / Empty State ───────────────────────────── */
.loading-container,
.empty-state {
  text-align: center;
  padding: 50px 0;
  color: #aaa;
}

.spinner-wrap i { color: #2c4a7c; }
.spinner-wrap p { margin-top: 12px; font-size: 14px; color: #777; }

.empty-icon {
  font-size: 56px;
  color: #ddd;
  display: block;
  margin-bottom: 12px;
}

.empty-state h4 { color: #555; }
.empty-state p { color: #999; font-size: 14px; }

/* ─── Table ───────────────────────────────────────────── */
.logs-table {
  font-size: 13px;
}

.table-header-row {
  background-color: #1a2a4a !important;
  color: #fff;
}

.table-header-row th {
  background-color: #1a2a4a !important;
  color: #fff !important;
  border-color: #2c4a7c !important;
  padding: 10px 8px !important;
  font-size: 13px;
  white-space: nowrap;
}

.table-footer-row th {
  background-color: #f0f4f8;
  font-weight: 700;
  font-size: 14px;
  padding: 10px 8px !important;
}

.total-cell {
  color: #1a7a1a;
  font-size: 15px;
}

/* Row tinting */
.row-reprint {
  background-color: #fff8f0 !important;
}

/* Column sizing */
.col-num    { width: 40px; }
.col-amount { width: 100px; }
.col-date   { width: 100px; white-space: nowrap; }
.col-time   { width: 90px; white-space: nowrap; }
.col-type   { width: 110px; }
.col-status { width: 130px; }

.col-status .badge {
  display: inline-block;
  margin: 2px 0;
}

/* ─── Badges ──────────────────────────────────────────── */
.badge {
  display: inline-block;
  padding: 4px 9px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.4;
}

.badge-shipping {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.badge-second-shipping {
  background-color: #fff3cd;
  color: #856404;
  border: 1px solid #ffeeba;
}

.badge-delivery {
  background-color: #cce5ff;
  color: #004085;
  border: 1px solid #b8daff;
}

.badge-first {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.badge-reprint {
  background-color: #ffe5b4;
  color: #7a4800;
  border: 1px solid #ffd78a;
}

.badge-view-only {
  background-color: #f1e6fb;
  color: #5a2d82;
  border: 1px solid #dcc4f2;
}

.badge-save-and-view {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.badge-delivery-print {
  background-color: #cce5ff;
  color: #004085;
  border: 1px solid #b8daff;
}

/* ─── Print Styles ────────────────────────────────────── */
.print-only { display: none; }

@media print {
  .no-print { display: none !important; }
  .print-only { display: flex !important; align-items: center; gap: 16px; margin-bottom: 20px; }
  .print-logs-header { background: #fff !important; color: #000 !important; border-bottom: 2px solid #333; }
  .header-title { color: #000 !important; }
  .table-header-row th { background-color: #333 !important; color: #fff !important; -webkit-print-color-adjust: exact; }
  body { font-size: 12px; }
  .print-logs-page { padding: 0; }
  .panel { border: none !important; box-shadow: none !important; }
}

@media (max-width: 768px) {
  .print-logs-header { flex-direction: column; align-items: flex-start; gap: 12px; }
  .summary-bar { flex-direction: column; }
  .summary-item { min-width: auto; width: 100%; }
  .filter-row { flex-direction: column; align-items: flex-start; }
  .date-input, .type-input, .printed-by-select { width: 100% !important; }
  .col-user, .col-date { display: none; }
}
</style>



// WEBPACK FOOTER //
// src/components/PrintLogs.vue